/**
 * ProtectedRoute — auth gate for the Designer.
 *
 * On a failed session check this renders a real sign-in form (shared
 * SignInGate) rather than the diagnostic card it used to show. An author
 * whose session timed out mid-edit was previously told their "session check
 * failed", shown a host/cookie dump, and offered a button to the Console —
 * which meant leaving the tab, and the work in it. Now they sign in in place
 * and carry on; refreshUser re-checks the session without a reload, so
 * nothing unsaved is lost.
 *
 * The old diagnostics are still available under "Technical details" for the
 * real misconfiguration cases (cookie domain not deployed, CORS allowlist,
 * wrong host).
 */

import React from 'react';
import { SignInGate } from '@cloudsam/ui';
import { useAuth } from '../context/AuthContext';
import hanko from '../hanko-init';
import SamLogo from './SamLogo';

const SPINNER_STYLE_ID = 'protected-route-spinner';
function injectSpinnerStyle() {
    if (typeof document === 'undefined') return;
    if (document.getElementById(SPINNER_STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = SPINNER_STYLE_ID;
    style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
}

export default function ProtectedRoute({ children }) {
    const { loading, isAuthenticated, error, refreshUser } = useAuth();

    React.useEffect(() => { injectSpinnerStyle(); }, []);

    if (loading) {
        return (
            <div style={styles.center}>
                <div style={styles.spinner} />
                <p style={styles.muted}>Checking your session…</p>
            </div>
        );
    }

    if (!isAuthenticated) {
        const here = (typeof window !== 'undefined' && window.location) ? window.location.host : '(unknown host)';
        const reason = error ? String(error.message || error) : 'no session found';
        return (
            <SignInGate
                hanko={hanko}
                logo={<SamLogo size={56} showWordmark variant="onWhite" />}
                appName="Designer"
                onSignedIn={refreshUser}
                detail={`host:     ${here}\nreason:   ${reason}\nexpected: a Hanko cookie scoped to samsurveyor.com riding on a\n          credentialed call to api.samsurveyor.com/dbam/v1/me`}
            />
        );
    }

    return children;
}

const styles = {
    center: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#f7f8fa',
        fontFamily: 'system-ui, -apple-system, sans-serif',
    },
    spinner: {
        width: 32,
        height: 32,
        border: '3px solid #d8dde6',
        borderTopColor: '#1f3a93',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    muted: { color: '#6b7280', marginTop: 12, fontSize: 14 },
};
